import { ApolloClient } from "@apollo/client";
import { print } from "graphql";
import { from, throwError, Observable } from "rxjs";
import { mergeMap, map, catchError } from "rxjs/operators";
import "@opentelemetry/api";
import "../otel/TraceProvider.js";
import { applyLocationAttrs, applyUserAttrs, applyHttpRequestAttrs, applyHttpResponseAttrs } from "../otel/OtelSpan.js";
import "react";
import "react-error-boundary";
import "@opentelemetry/context-zone";
import "@opentelemetry/exporter-trace-otlp-http";
import "@opentelemetry/instrumentation";
import "@opentelemetry/instrumentation-document-load";
import "@opentelemetry/instrumentation-xml-http-request";
import "@opentelemetry/resources";
import "@opentelemetry/sdk-trace-web";
import "@opentelemetry/semantic-conventions";
const httpAttributesPrefix = "http";
const httpAttributesRequestPrefix = `${httpAttributesPrefix}.request`;
const GqlAttributes = {
  GRAPHQL_METHOD: `${httpAttributesRequestPrefix}.gql.type`,
  GRAPHQL_METHOD_NAME: `${httpAttributesRequestPrefix}.gql.query`
};
function applyGqlRequestAttrs(span, gqlQueryType, gqlQuery) {
  span?.setAttributes({
    [GqlAttributes.GRAPHQL_METHOD]: gqlQueryType,
    [GqlAttributes.GRAPHQL_METHOD_NAME]: gqlQuery
  });
}
class ApolloClientProxy extends ApolloClient {
  _tracingService;
  _userManager;
  _uri;
  constructor(options, tracingService, userManager) {
    super(options);
    this._uri = options?.uri;
    this._tracingService = tracingService;
    this._userManager = userManager;
  }
  query(options) {
    const tracer = this._tracingService.getTracer();
    return this.getUser().pipe(
      mergeMap(
        (user) => tracer.startActiveSpan("GRAPHQL QUERY", (span) => {
          applyLocationAttrs(span);
          applyUserAttrs(span, user.basicInfo);
          applyHttpRequestAttrs(span, this._uri, "POST", "xhr");
          applyGqlRequestAttrs(span, "QUERY", print(options.query));
          return from(super.query(options)).pipe(
            map((r) => {
              if (r.errors) {
                const message = this.getErrorMessageFromErrorResponse(r);
                applyHttpResponseAttrs(span, null, message);
                this._tracingService.endSpanFailed(span, message);
              } else {
                applyHttpResponseAttrs(span, "200", this.getErrorMessageFromQuerySuccessResponse(r));
                this._tracingService.endSpanOk(span);
              }
              return r;
            }),
            catchError((r) => {
              const message = this.getErrorMessageFromErrorResponse(r);
              applyHttpResponseAttrs(span, "500", message);
              this._tracingService.endSpanFailed(span, message);
              return throwError(r);
            })
          );
        })
      )
    ).toPromise();
  }
  mutate(options) {
    const tracer = this._tracingService.getTracer();
    return this.getUser().pipe(
      mergeMap(
        (user) => tracer.startActiveSpan("GRAPHQL MUTATION", (span) => {
          applyLocationAttrs(span);
          applyUserAttrs(span, user.basicInfo);
          applyHttpRequestAttrs(span, this._uri, "POST", "xhr");
          applyGqlRequestAttrs(span, "MUTATION", print(options.mutation));
          return from(super.mutate(options)).pipe(
            map((r) => {
              if (r.errors) {
                const message = this.getErrorMessageFromErrorResponse(r);
                applyHttpResponseAttrs(span, null, message);
                this._tracingService.endSpanFailed(span, message);
              } else {
                applyHttpResponseAttrs(span, "200", this.getErrorMessageFromMutationSuccessResponse(r));
                this._tracingService.endSpanOk(span);
              }
              return r;
            }),
            catchError((r) => {
              const message = this.getErrorMessageFromErrorResponse(r);
              applyHttpResponseAttrs(span, "500", message);
              this._tracingService.endSpanFailed(span, message);
              return throwError(r);
            })
          );
        })
      )
    ).toPromise();
  }
  subscribeRx(options) {
    return new Observable(
      (subscriber) => this.subscribe(options).subscribe({
        next: (args) => {
          return subscriber.next(args);
        },
        error: (...args) => {
          return subscriber.error(args);
        },
        complete: () => {
          return subscriber.complete();
        }
      })
    );
  }
  subscribe(options) {
    return super.subscribe(options);
  }
  getUser() {
    return this._userManager.getUserExtended();
  }
  getErrorMessageFromErrorResponse(r) {
    return r.error || r.errors || r.networkError || r.networkErrors || r.graphQLErrors ? JSON.stringify({
      error: r.error,
      errors: r.errors,
      networkError: r.networkErrors,
      graphQLErrors: r.graphQLErrors
    }) : void 0;
  }
  getErrorMessageFromQuerySuccessResponse(r) {
    return r.error || r.errors ? JSON.stringify({ error: r.error, errors: r.errors }) : void 0;
  }
  getErrorMessageFromMutationSuccessResponse(r) {
    return r.errors ? JSON.stringify({ errors: r.errors }) : void 0;
  }
}
export {
  ApolloClientProxy
};
//# sourceMappingURL=ApolloClientProxy.js.map
